import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { RefreshCw, ExternalLink, AlertTriangle, CalendarDays } from 'lucide-react';
import WoRedirects from './WoRedirects';
import { useAuth } from '../App';
import { DayStrip, stripDays } from './dailies/DayStrip';
import { DayView } from './dailies/DayView';
import { BackupsPanel } from './dailies/BackupsPanel';

const SYNC_ROLES = ['super_admin', 'admin', 'office_admin', 'dispatch', 'project_manager', 'project_manager_estimator'];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const todayET = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Toronto' });
const fmtDay = (d) => new Date(`${d}T00:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

export default function ManpowerSheet() {
  const { user } = useAuth();
  const canEdit = SYNC_ROLES.includes(user?.role);
  const [status, setStatus] = useState(null);
  const [monthKey, setMonthKey] = useState(todayET().slice(0, 7));
  const [date, setDate] = useState(todayET());
  const [info, setInfo] = useState({});
  const [syncing, setSyncing] = useState(false);

  const loadStatus = useCallback(async () => {
    try { const s = await axios.get(`${API}/manpower/status`, auth()); setStatus(s.data); } catch { /* ignore */ }
  }, []);
  const loadRange = useCallback(async () => {
    const days = stripDays(monthKey);
    try { const r = await axios.get(`${API}/dailies/range`, { ...auth(), params: { start: days[0], end: days[days.length - 1] } }); setInfo(r.data.days || {}); } catch { setInfo({}); }
  }, [monthKey]);
  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => { loadRange(); }, [loadRange]);
  const [dayKey, setDayKey] = useState(0);
  const refresh = () => { loadRange(); setDayKey(k => k + 1); };

  const sync = async () => {
    setSyncing(true);
    try {
      const r = await axios.post(`${API}/manpower/sync`, {}, { ...auth(), timeout: 180000 });
      toast.success(`Sheet read — ${r.data.days} days, ${r.data.jobs} jobs, ${r.data.assignments} people-days${r.data.unmatched.length ? ` · ${r.data.unmatched.length} unmatched name${r.data.unmatched.length !== 1 ? 's' : ''}` : ''}`);
      await loadStatus(); refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Sync failed'); }
    setSyncing(false);
  };
  const unmatched = status?.last_summary?.unmatched || [];

  return (
    <div className="space-y-4" data-testid="manpower-sheet-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><CalendarDays className="w-6 h-6 text-sky-700" />Dailies</h1>
          <p className="text-slate-500 text-sm">The day tabs — who goes where, with what. Blair's Google sheet is read every 15 minutes and wins on any WO/day it covers; anything you add here fills the gaps and lands on the Schedule right away.</p>
          {status?.last_sync_at && <p className="text-xs text-slate-500 mt-1" data-testid="mp-last-sync">Sheet last read {new Date(status.last_sync_at).toLocaleString()} ({status.last_trigger}){status.last_error ? ` · last error: ${status.last_error}` : ''}{status.headcount ? ` · Current Manpower: ${status.headcount}` : ''}</p>}
        </div>
        <div className="flex items-center gap-2">
          {status?.sheet_id && (
            <a href={`https://docs.google.com/spreadsheets/d/${status.sheet_id}/edit`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-slate-700 hover:text-slate-900 border border-slate-300 rounded-lg px-3 py-2 bg-white" data-testid="mp-open-sheet">
              <ExternalLink className="w-4 h-4" /> Open sheet
            </a>
          )}
          {canEdit && (
            <button onClick={sync} disabled={syncing} className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-500 disabled:opacity-60 rounded-lg px-3 py-2" data-testid="mp-sync-now">
              <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} /> {syncing ? 'Reading…' : 'Read sheet now'}
            </button>
          )}
        </div>
      </div>

      {unmatched.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4" data-testid="mp-unmatched">
          <p className="text-sm font-semibold text-red-800 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> {unmatched.length} name{unmatched.length !== 1 ? 's' : ''} on the sheet don't match a 365Ops user</p>
          <p className="text-xs text-red-800 mt-1">{unmatched.join(' · ')}</p>
          <p className="text-[11px] text-slate-500 mt-1.5">Fix: add them under HR → Team (or correct the spelling on the sheet). The next read picks them up automatically.</p>
        </div>
      )}

      <DayStrip monthKey={monthKey} onMonth={setMonthKey} date={date} onDate={setDate} info={info} />

      <DayView key={`${date}-${dayKey}`} date={date} canEdit={canEdit} onChanged={loadRange} />

      <BackupsPanel monthKey={monthKey} />

      {status?.last_summary?.schedule && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 text-sm" data-testid="mp-schedule-mirror">
          <p className="text-slate-800 font-semibold">Schedule grid mirror</p>
          <p className="text-xs text-slate-500 mt-0.5">
            Every job on the dailies is a bar on the Schedule — last sheet read: {status.last_summary.schedule.created} added, {status.last_summary.schedule.updated} updated, {status.last_summary.schedule.deleted} removed
            {status.last_summary.off_days ? ` · ${status.last_summary.off_days} off-days sent to the HR vacation calendar` : ''}
            {status.last_summary.people_alerted ? ` · ${status.last_summary.people_alerted} people's days changed (logged only — crew are not pinged until dispatch sends texts)` : ''}
          </p>
          {status.last_summary.schedule.skipped?.length > 0 && (
            <div className="mt-2 text-xs text-amber-800" data-testid="mp-schedule-skipped">
              <p className="font-semibold">Not put on the Schedule:</p>
              {status.last_summary.schedule.skipped.slice(0, 8).map((s, i) => <p key={i}>• {fmtDay(s.date)} — {s.work_order}{s.customer ? ` (${s.customer})` : ''}: {s.reason}</p>)}
            </div>
          )}
        </div>
      )}

      <WoRedirects canEdit={canEdit} />

    </div>
  );
}
